import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  viewChild,
  WritableSignal,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CommonModule, DecimalPipe } from "@angular/common";
import { format, startOfYear, subDays } from "date-fns";
import { Chart, registerables } from "chart.js";
import { HeaderDialogComponent } from "../../../../shared/modules/dialog/header-dialog/header-dialog.component";
import { SharedModule } from "../../../../shared/shared.module";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { MultiCheckboxComponent } from "../../../../shared/modules/multi-checkbox/multi-checkbox.component";
import { ExcelExportButtonComponent } from "../../../../shared/components/excel-export-button/excel-export-button.component";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { firstValueFrom, forkJoin } from "rxjs";
import {
  exportEvolutionValeurExcel,
  exportEvolutionSnapshotExcel,
} from "./evolution-valeur-excel.utils";
import {
  ExcelExportConfirmDialogComponent,
  ExcelExportConfirmData,
  ExcelExportConfirmResult,
} from "../../../../shared/components/excel-export-confirm-dialog/excel-export-confirm-dialog.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import {
  EVOLUTION_VALEUR_DATA,
  EvolutionDimension,
  EvolutionGranularity,
  VALEUR_PAR_FAMILLE_DATA,
} from "../../models/data.model";
import { WidgetType } from "../../models/dashboard.model";
import {
  BackfillRange,
  WidgetDataApiService,
} from "../../services/widget-data-api.service";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { SnackService } from "../../../../shared/services/snack-service.service";
import { TranslateUtils } from "../../../../shared/utils/translate.utils";

Chart.register(...registerables);

/**
 * Dialog "Voir tout" du widget EVOLUTION_VALEUR. Permet de :
 * - Choisir la **plage** (1m / 3m / 6m / exercice / 1a)
 * - Choisir la **granularité** temporelle (jour / semaine / mois /
 *   trimestre / année) — les options sont filtrées en fonction de la plage
 *   pour éviter les combinaisons illisibles (ex : year-by-year sur 1m)
 * - Choisir la **dimension** de découpage (total / famille / sous-famille /
 *   article / entrepôt / propriétaire)
 *
 * Affiche un line chart multi-séries avec les TOP N dimensions, un tableau
 * des valeurs courantes, et propose de relancer le calcul si l'historique
 * stocké est plus court que la plage demandée.
 *
 * # Données
 * Self-fetching via `WidgetDataApiService.loadEvolutionValeur()`. Le
 * backfill est délégué au composant parent (le bouton sync du widget
 * compact reste la source unique du recalcul) — depuis ce dialog, on
 * propose un CTA qui appelle `runBackfill()` puis refetch.
 */

interface DialogData {
  initialRange: BackfillRange;
}

const TOP_N = 7;
const PALETTE = [
  "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
  "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf",
];

/** Libellé du bucket "stock directement sur l'entrepôt parent" (subEntrepot
 *  null). Rendu en dernier sous chaque entrepôt. */
const STOCK_DIRECT_LABEL = "Stock direct entrepôt";
/** Séparateur des clés composites d'expansion (entrepot¦sous-zone¦article). */
const KEY_SEP = " ";

interface LotNode {
  lotNumero: string;
  quantite: number;
  unite: string;
  total: number;
  coutUnitaire: number;
  dluo: string | null;
}
interface ArticleNode {
  article: string;
  total: number;
  quantite: number;
  lots: LotNode[];
}
interface SubZoneNode {
  subEntrepot: string;
  total: number;
  quantite: number;
  articles: ArticleNode[];
}

@Component({
  selector: "vl-evolution-valeur-dialog",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    HeaderDialogComponent,
    ChipsToggleComponent,
    MultiCheckboxComponent,
    ExcelExportButtonComponent,
    MatIcon,
    MatTooltip,
    DecimalPipe,
  ],
  templateUrl: "./evolution-valeur-dialog.component.html",
  styleUrl: "./evolution-valeur-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvolutionValeurDialogComponent implements OnDestroy, AfterViewInit {
  data = inject<DialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<EvolutionValeurDialogComponent>);

  private api = inject(WidgetDataApiService);
  private snack = inject(SnackService);
  private dialogs = inject(DialogsService);

  /** Plage personnalisée — alternative à `range()`. Utilisée seulement à
   *  l'export. Quand non-null, on envoie `customStart`/`customEnd` à
   *  l'API pour le snapshot Excel (le widget compact reste sur la plage
   *  glissante par défaut). */
  exportCustomRange = signal<{ from: string; to: string } | null>(null);

  canvasRef = viewChild<ElementRef>("chart");

  range = signal<BackfillRange>(this.data.initialRange ?? "3m");
  granularity = signal<EvolutionGranularity>("day");
  dimension = signal<EvolutionDimension>("total");

  /** Filtre optionnel par dimension : liste des `dimensionKey` à conserver.
   *  Vide = aucun filtre (TOP N + "Autres" comme avant). */
  selectedKeys = signal<string[]>([]);

  /** Drill-down entrepôt → sous-zone → article → lot. Le détail vient de
   *  `currentValeur` (état actuel) — il n'y a pas d'historique fin, le chart
   *  reste agrégé au niveau entrepôt parent. Trois niveaux dépliables, chacun
   *  avec son Set de clés ouvertes :
   *  - entrepôt : clé = nom de l'entrepôt
   *  - sous-zone : clé = `entrepot¦subEntrepot`
   *  - article : clé = `entrepot¦subEntrepot¦article` */
  expandedEntrepots = signal<Set<string>>(new Set());
  expandedSubZones = signal<Set<string>>(new Set());
  expandedArticles = signal<Set<string>>(new Set());

  loading = signal<boolean>(true);
  refreshing = signal<boolean>(false);
  exporting = signal<boolean>(false);
  payload = signal<EVOLUTION_VALEUR_DATA | null>(null);
  /** Snapshot courant `VALEUR_PAR_FAMILLE` — utilisé pour le drill-down par
   *  sous-zone. Chargé seulement quand dimension = entrepot. */
  currentValeur = signal<VALEUR_PAR_FAMILLE_DATA | null>(null);
  chart: Chart | null = null;

  // ─── Sélection d'une date précise (état du stock à cette date) ─────────────
  /** Date de référence choisie (yyyy-MM-dd) ou null. Lit la valeur TOTALE au
   *  snapshot ≤ cette date dans la série déjà chargée (même logique que le
   *  survol du widget). Hors plage → élargir via le sélecteur de plage. */
  refDate = signal<string | null>(null);

  /** Bornes du sélecteur = première / dernière date de la série chargée. */
  minRefDate = computed<string | null>(() => {
    const t = this.payload()?.total;
    return t && t.length ? t[0].date : null;
  });
  maxRefDate = computed<string | null>(() => {
    const t = this.payload()?.total;
    return t && t.length ? t[t.length - 1].date : null;
  });

  /** Valeur TOTALE au snapshot le plus récent ≤ refDate (null si aucun). */
  refValue = computed<number | null>(() => {
    const d = this.refDate();
    const t = this.payload()?.total;
    if (!d || !t?.length) return null;
    let val: number | null = null;
    for (const p of t) {
      if (p.date <= d) val = p.total;
      else break;
    }
    return val;
  });

  /** Libellé formaté de la valeur à la date de référence (ou null). */
  refValueLabel = computed<string | null>(() => {
    const v = this.refValue();
    return v == null ? null : WidgetHelperService.formatNumeric(v) + " €";
  });

  onRefDateChange(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.refDate.set(v || null);
  }
  clearRefDate(): void {
    this.refDate.set(null);
    this.compareDate.set(null); // quitter le mode instantané annule la comparaison
  }

  /** Date de référence affichée en JJ/MM/AAAA (ou null). */
  refDateDisplay = computed<string | null>(() => {
    const rd = this.refDate();
    return rd ? format(new Date(rd + "T00:00:00"), "dd/MM/yyyy") : null;
  });

  /** Mode d'analyse : par PÉRIODE (courbe + plage/granularité) ou à DATE FIXE
   *  (instantané + comparaison de 2 dates). Un seul jeu de contrôles visible à
   *  la fois → pas d'ambiguïté « période vs date ». */
  mode = signal<"periode" | "dateFixe">("periode");
  viewModeElements = [
    { name: "Période", value: "periode" as const },
    { name: "Date fixe", value: "dateFixe" as const },
  ];
  /** Plage/granularité mémorisées pour restauration au retour en mode période. */
  private savedPeriod: {
    range: BackfillRange;
    granularity: EvolutionGranularity;
  } | null = null;

  setMode(m: "periode" | "dateFixe"): void {
    if (m === this.mode()) return;
    if (m === "dateFixe") {
      // Fenêtre large + granularité jour : les 2 sélecteurs de date peuvent
      // remonter loin et se résoudre au snapshot journalier exact.
      this.savedPeriod = { range: this.range(), granularity: this.granularity() };
      // 6m + jour : la granularité "jour" est autorisée pour 6m (pas pour 1y,
      // où l'effet de garde la ramènerait à "semaine" → résolution au jour KO).
      this.range.set("6m");
      this.granularity.set("day");
      if (!this.refDate()) this.refDate.set(format(new Date(), "yyyy-MM-dd"));
    } else {
      // Retour période : on annule les dates fixes et on restaure la plage.
      this.refDate.set(null);
      this.compareDate.set(null);
      if (this.savedPeriod) {
        this.range.set(this.savedPeriod.range);
        this.granularity.set(this.savedPeriod.granularity);
      }
    }
    this.mode.set(m);
  }

  /** Mode instantané = date fixe. Pilote l'histogramme + le tableau à-la-date. */
  snapshotMode = computed<boolean>(() => this.mode() === "dateFixe");

  // ─── Comparaison de 2 dates (mode instantané) ──────────────────────────────
  /** 2ᵉ date optionnelle. En mode instantané, si renseignée, la colonne de
   *  droite devient « Écart vs <date B> » (état à A − état à B) et le graphe
   *  passe en barres groupées A vs B. */
  compareDate = signal<string | null>(null);
  compareDateDisplay = computed<string | null>(() => {
    const cd = this.compareDate();
    return cd ? format(new Date(cd + "T00:00:00"), "dd/MM/yyyy") : null;
  });
  /** Comparaison active = mode instantané ET 2ᵉ date renseignée. */
  hasCompare = computed<boolean>(
    () => this.snapshotMode() && this.compareDate() != null,
  );

  onCompareDateChange(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.compareDate.set(v || null);
  }
  clearCompareDate(): void {
    this.compareDate.set(null);
  }

  rangeElements: { name: string; value: BackfillRange }[] = [
    { name: "1M", value: "1m" },
    { name: "3M", value: "3m" },
    { name: "6M", value: "6m" },
    { name: "1A", value: "1y" },
    { name: "YTD", value: "ytd" },
    { name: "Exercice", value: "exercice" },
    { name: "Exercice passé", value: "exerciceN1" },
  ];

  /** Granularités proposées : on filtre celles qui n'ont pas de sens pour
   *  la plage. Ex : `year` n'a pas d'intérêt sur une plage < 2 ans, on
   *  l'exclut pour éviter un seul gros point. */
  granularityElements = computed(() => {
    const r = this.range();
    const all: { name: string; value: EvolutionGranularity }[] = [
      { name: "Jour", value: "day" },
      { name: "Semaine", value: "week" },
      { name: "Mois", value: "month" },
      { name: "Trimestre", value: "quarter" },
      { name: "Année", value: "year" },
    ];
    if (r === "1m") return all.filter((g) => ["day", "week"].includes(g.value));
    if (r === "3m") return all.filter((g) => ["day", "week", "month"].includes(g.value));
    if (r === "6m") return all.filter((g) => ["day", "week", "month"].includes(g.value));
    if (r === "ytd" || r === "exercice" || r === "exerciceN1")
      return all.filter((g) => ["day", "week", "month", "quarter"].includes(g.value));
    if (r === "1y")
      return all.filter((g) => ["week", "month", "quarter", "year"].includes(g.value));
    return all;
  });

  dimensionElements: { name: string; value: EvolutionDimension }[] = [
    { name: "Total", value: "total" },
    { name: "Famille", value: "famille" },
    { name: "Sous-famille", value: "sous_famille" },
    { name: "Article", value: "article" },
    { name: "Entrepôt", value: "entrepot" },
    { name: "Propriétaire", value: "owner" },
  ];

  /** Items proposés dans le multi-select de filtre, dérivés des séries
   *  effectivement chargées pour la dimension courante. Triés par valeur de
   *  fin décroissante pour mettre en avant les contributeurs majeurs. */
  filterElements = computed<{ name: string; value: string }[]>(() => {
    const d = this.payload();
    if (!d || this.dimension() === "total") return [];
    return [...d.series]
      .sort((a, b) => this.currentValue(b.points) - this.currentValue(a.points))
      .map((s) => ({ name: s.dimensionLabel, value: s.dimensionKey }));
  });

  /** Libellé du bouton multi-select : suit la dimension courante. */
  filterLabel = computed<string>(() => {
    const dim = this.dimension();
    return (
      this.dimensionElements.find((d) => d.value === dim)?.name ?? "Filtrer"
    );
  });

  /** Arbre de drill-down entrepôt → sous-zone → article → lot, construit
   *  depuis `currentValeur().details` (une ligne par lot). Uniquement quand
   *  dimension = entrepot. Les stocks directement sur l'entrepôt parent
   *  (`subEntrepot` null) sont regroupés sous "Stock direct entrepôt", rendu
   *  en DERNIER. Les totaux de chaque niveau sont agrégés depuis les lots. */
  entrepotTree = computed<Map<string, SubZoneNode[]>>(() => {
    const data = this.currentValeur();
    if (!data || this.dimension() !== "entrepot") return new Map();

    // entrepot → subEntrepot → article → lots[]
    const root = new Map<string, Map<string, Map<string, LotNode[]>>>();
    for (const d of data.details ?? []) {
      const subKey = d.subEntrepot ?? STOCK_DIRECT_LABEL;
      const ent = root.get(d.entrepot) ?? new Map();
      const sub = ent.get(subKey) ?? new Map();
      const lots = sub.get(d.article) ?? [];
      lots.push({
        lotNumero: d.lotNumero,
        quantite: d.quantite,
        unite: d.unite,
        total: d.total,
        coutUnitaire: d.coutUnitaire,
        dluo: d.dluo,
      });
      sub.set(d.article, lots);
      ent.set(subKey, sub);
      root.set(d.entrepot, ent);
    }

    const sum = (arr: { total: number; quantite: number }[]) =>
      arr.reduce(
        (acc, x) => {
          acc.total += x.total;
          acc.quantite += x.quantite;
          return acc;
        },
        { total: 0, quantite: 0 },
      );

    const result = new Map<string, SubZoneNode[]>();
    for (const [ent, subs] of root.entries()) {
      const subNodes: SubZoneNode[] = [];
      for (const [subEntrepot, articles] of subs.entries()) {
        const artNodes: ArticleNode[] = [];
        for (const [article, lots] of articles.entries()) {
          const sortedLots = [...lots].sort((a, b) => b.total - a.total);
          const { total, quantite } = sum(sortedLots);
          artNodes.push({ article, total, quantite, lots: sortedLots });
        }
        artNodes.sort((a, b) => b.total - a.total);
        const { total, quantite } = sum(artNodes);
        subNodes.push({ subEntrepot, total, quantite, articles: artNodes });
      }
      // "Stock direct entrepôt" en dernier, le reste par valeur décroissante.
      subNodes.sort((a, b) => {
        if (a.subEntrepot === STOCK_DIRECT_LABEL) return 1;
        if (b.subEntrepot === STOCK_DIRECT_LABEL) return -1;
        return b.total - a.total;
      });
      result.set(ent, subNodes);
    }
    return result;
  });

  // ─── Expansion (3 niveaux) ─────────────────────────────────────────────────

  private subZoneKey(entrepot: string, sub: string): string {
    return entrepot + KEY_SEP + sub;
  }
  private articleKey(entrepot: string, sub: string, article: string): string {
    return entrepot + KEY_SEP + sub + KEY_SEP + article;
  }

  private toggleIn(sig: WritableSignal<Set<string>>, key: string) {
    const cur = new Set(sig());
    if (cur.has(key)) cur.delete(key);
    else cur.add(key);
    sig.set(cur);
  }

  /** Vrai si la ligne entrepôt peut être dépliée (dimension entrepot + des
   *  détails existent pour cet entrepôt). */
  canExpandRow(entrepotName: string): boolean {
    return (
      this.dimension() === "entrepot" && this.entrepotTree().has(entrepotName)
    );
  }

  onRowClick(entrepotName: string) {
    if (this.canExpandRow(entrepotName)) {
      this.toggleIn(this.expandedEntrepots, entrepotName);
    }
  }
  isExpanded(entrepotName: string): boolean {
    return this.expandedEntrepots().has(entrepotName);
  }
  getSubZones(entrepotName: string): SubZoneNode[] {
    return this.entrepotTree().get(entrepotName) ?? [];
  }

  toggleSubZone(entrepot: string, sub: string) {
    this.toggleIn(this.expandedSubZones, this.subZoneKey(entrepot, sub));
  }
  isSubZoneExpanded(entrepot: string, sub: string): boolean {
    return this.expandedSubZones().has(this.subZoneKey(entrepot, sub));
  }

  toggleArticle(entrepot: string, sub: string, article: string) {
    this.toggleIn(
      this.expandedArticles,
      this.articleKey(entrepot, sub, article),
    );
  }
  isArticleExpanded(entrepot: string, sub: string, article: string): boolean {
    return this.expandedArticles().has(this.articleKey(entrepot, sub, article));
  }

  // ─── Affichage ───────────────────────────────────────────────────────────

  totalValeur = computed(() => {
    const d = this.payload();
    if (!d) return "—";
    // En mode date fixe, l'en-tête reflète le total À LA DATE sélectionnée
    // (cohérent avec le tableau), et non la valeur de fin de fenêtre.
    if (this.snapshotMode()) {
      const v = this.refValue();
      return v == null ? "—" : WidgetHelperService.formatNumeric(v) + " €";
    }
    return WidgetHelperService.formatNumeric(d.delta.endValue) + " €";
  });

  deltaText = computed(() => {
    const d = this.payload();
    if (!d) return "";
    const sign = d.delta.absolute >= 0 ? "+" : "−";
    const abs = Math.abs(d.delta.absolute);
    const pct = d.delta.percent;
    const pctStr = pct == null ? "" : ` (${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%)`;
    return `${sign}${WidgetHelperService.formatNumeric(abs)} €${pctStr}`;
  });

  deltaClass = computed<"positive" | "negative" | "neutral">(() => {
    const d = this.payload();
    if (!d || d.delta.absolute === 0) return "neutral";
    return d.delta.absolute > 0 ? "positive" : "negative";
  });

  /** Top N dimensions par valeur de fin, plus une ligne "Autres" agrégée
   *  si plus de N. Pour la dimension `total` on a déjà une seule série.
   *  Si `selectedKeys` est non vide, on bypass le TOP N et on n'affiche
   *  QUE les éléments sélectionnés (pas de regroupement "Autres"). */
  visibleSeries = computed(() => {
    const d = this.payload();
    if (!d) return [];
    if (this.dimension() === "total") {
      return d.series.map((s, i) => ({ ...s, color: PALETTE[i % PALETTE.length] }));
    }
    const sorted = [...d.series].sort(
      (a, b) => this.currentValue(b.points) - this.currentValue(a.points),
    );
    const selected = this.selectedKeys();
    if (selected.length > 0) {
      const wanted = new Set(selected);
      return sorted
        .filter((s) => wanted.has(s.dimensionKey))
        .map((s, i) => ({ ...s, color: PALETTE[i % PALETTE.length] }));
    }
    const top = sorted.slice(0, TOP_N).map((s, i) => ({
      ...s,
      color: PALETTE[i % PALETTE.length],
    }));
    if (sorted.length > TOP_N) {
      // Agrégation "Autres" — on additionne points par date.
      const dateMap = new Map<string, number>();
      for (const s of sorted.slice(TOP_N)) {
        for (const p of s.points) {
          dateMap.set(p.date, (dateMap.get(p.date) ?? 0) + p.total);
        }
      }
      const otherPoints = Array.from(dateMap.entries())
        .map(([date, total]) => ({ date, total, quantite: 0 }))
        .sort((a, b) => a.date.localeCompare(b.date));
      top.push({
        dimensionKey: "__OTHERS__",
        dimensionLabel: `Autres (${sorted.length - TOP_N})`,
        points: otherPoints,
        color: "#cccccc",
      });
    }
    return top;
  });

  /** Toutes les dates uniques de la fenêtre, triées ASC — sert de `labels`
   *  pour le chart et de colonnes pour le tableau. */
  allDates = computed(() => {
    const set = new Set<string>();
    for (const s of this.visibleSeries()) {
      for (const p of s.points) set.add(p.date);
    }
    return Array.from(set).sort();
  });

  /** Date de référence "maintenant" = date la plus récente parmi TOUTES les
   *  séries du payload (≈ endDate). Dérivée du payload seul (pas de
   *  `visibleSeries`) pour éviter un cycle de computed avec le tri/TOP N. */
  private currentDate = computed<string | null>(() => {
    const d = this.payload();
    if (!d) return null;
    let max: string | null = null;
    for (const s of d.series) {
      const last = s.points[s.points.length - 1]?.date;
      if (last && (max === null || last > max)) max = last;
    }
    return max;
  });

  /** Date de référence effective des valeurs affichées :
   *  - date choisie (résolue au snapshot le plus récent ≤ elle), en mode instantané ;
   *  - sinon la date "maintenant" (fin de fenêtre).
   *  Résoudre au snapshot réel garantit un `p.date === anchor` exact → on
   *  conserve la sémantique "dimension vidée = 0" (pas de forward-fill). */
  private anchorDate = computed<string | null>(() => {
    const rd = this.refDate();
    if (!rd) return this.currentDate();
    const dates = this.payload()?.total ?? [];
    let res: string | null = null;
    for (const p of dates) {
      if (p.date <= rd) res = p.date;
      else break;
    }
    return res ?? this.currentDate();
  });

  /** Snapshot ≤ 2ᵉ date (comparaison), ou null. */
  private compareAnchorDate = computed<string | null>(() => {
    const cd = this.compareDate();
    if (!cd) return null;
    const dates = this.payload()?.total ?? [];
    let res: string | null = null;
    for (const p of dates) {
      if (p.date <= cd) res = p.date;
      else break;
    }
    return res;
  });

  needsBackfill = computed<boolean>(() => {
    const d = this.payload();
    if (!d) return false;
    if ((d.total?.length ?? 0) === 0) return true;
    if (d.availableSince && d.availableSince > d.startDate) return true;
    return false;
  });

  constructor() {
    // Refetch sur changement de range / granularity / dimension
    effect(
      () => {
        this.range();
        this.granularity();
        this.dimension();
        this.fetch();
      },
      { allowSignalWrites: true },
    );

    // (Re)build le chart. Dépend aussi de `refDate` : bascule courbe ↔ barres
    // (mode instantané) quand une date précise est sélectionnée/effacée.
    effect(
      () => {
        const series = this.visibleSeries();
        const labels = this.allDates();
        this.refDate(); // dépendance : rebuild au changement de date
        this.compareDate(); // dépendance : rebuild quand on ajoute/retire la comparaison
        if (this.canvasRef() && series.length) {
          this.buildChart(series, labels);
        }
      },
      { allowSignalWrites: true },
    );

    // Si la plage change, ajuste la granularité par défaut au plus large
    // de ce que la plage permet — évite l'erreur "granularité incompatible".
    effect(
      () => {
        const allowed = this.granularityElements().map((g) => g.value);
        if (!allowed.includes(this.granularity())) {
          this.granularity.set(allowed[0]);
        }
      },
      { allowSignalWrites: true },
    );

    // Reset du filtre quand la dimension change : les `dimensionKey`
    // disponibles sont propres à chaque dimension, garder une sélection
    // d'articles quand on bascule sur "Entrepôt" n'a aucun sens.
    effect(
      () => {
        this.dimension();
        this.selectedKeys.set([]);
        this.expandedEntrepots.set(new Set());
        this.expandedSubZones.set(new Set());
        this.expandedArticles.set(new Set());
      },
      { allowSignalWrites: true },
    );

    // Charge le snapshot VALEUR_PAR_FAMILLE courant — source du drill-down
    // par sous-zone (PDC, expé, NC) en dimension entrepot. On le fetch
    // dès l'ouverture du dialog (et pas seulement quand dimension change),
    // pour qu'il soit prêt si l'utilisateur passe sur "Entrepôt" plus tard.
    this.fetchCurrentValeur();

    // Retry si la première fetch a échoué (réseau, back en restart) et
    // que l'utilisateur passe en dimension entrepot.
    effect(
      () => {
        if (this.dimension() === "entrepot" && !this.currentValeur()) {
          this.fetchCurrentValeur();
        }
      },
      { allowSignalWrites: true },
    );
  }

  /** Fetch idempotent — peut être rappelée si la première a foiré (réseau,
   *  back qui redémarre…). */
  private fetchCurrentValeur() {
    this.api.loadOne(WidgetType.VALEUR_PAR_FAMILLE).subscribe({
      next: (res) => {
        const d = res?.data?.data as VALEUR_PAR_FAMILLE_DATA;
        this.currentValeur.set(d ?? null);
      },
      error: () => {
        // Retry automatique si l'utilisateur passe en dimension entrepot
        // alors qu'on n'a pas réussi à charger (cf. effect ci-dessous).
      },
    });
  }

  ngAfterViewInit(): void {
    // Le effect de rebuild peut tirer avant viewchild prêt, on retrigger
    if (this.canvasRef() && this.visibleSeries().length) {
      this.buildChart(this.visibleSeries(), this.allDates());
    }
  }

  // ─── Données ─────────────────────────────────────────────────────────────

  private resolveRange(range: BackfillRange): {
    startDate: string;
    endDate: string;
  } {
    const today = new Date();
    let endDate = format(today, "yyyy-MM-dd");
    let startDate: string;
    switch (range) {
      case "1m": startDate = format(subDays(today, 30), "yyyy-MM-dd"); break;
      case "3m": startDate = format(subDays(today, 90), "yyyy-MM-dd"); break;
      case "6m": startDate = format(subDays(today, 180), "yyyy-MM-dd"); break;
      case "1y": startDate = format(subDays(today, 365), "yyyy-MM-dd"); break;
      case "ytd":
        startDate = format(startOfYear(today), "yyyy-MM-dd");
        break;
      case "exercice":
        startDate = format(startOfYear(today), "yyyy-MM-dd");
        endDate = `${today.getFullYear()}-12-31`;
        break;
      case "exerciceN1": {
        const y = today.getFullYear() - 1;
        startDate = `${y}-01-01`;
        endDate = `${y}-12-31`;
        break;
      }
    }
    return { startDate, endDate };
  }

  fetch() {
    this.loading.set(true);
    const { startDate, endDate } = this.resolveRange(this.range());
    this.api
      .loadEvolutionValeur(this.granularity(), this.dimension(), startDate, endDate)
      .subscribe({
        next: (data) => {
          this.payload.set(data);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  /**
   * Export Excel multi-feuilles : refait un fetch pour chaque dimension
   * (parallèle), construit le workbook (1 feuille par dim + chart PNG +
   * Synthèse + Données brutes) et déclenche le téléchargement.
   */
  /** Ouvre le dialog de confirmation puis lance l'export avec la plage
   *  validée (preset, ou plage personnalisée si l'utilisateur a basculé). */
  async exportExcel() {
    if (this.exporting()) return;

    // ─── Mode DATE FIXE : export "état des stocks à la date" (+ comparaison
    //     si 2ᵉ date). Pas de popup de période — les dates sont déjà choisies.
    if (this.snapshotMode()) {
      const a = this.refDate();
      if (!a) return;
      const b = this.compareDate();
      this.exporting.set(true);
      // Fenêtre couvrant les dates + marge 30j (résolution au snapshot ≤).
      const dts = [a, ...(b ? [b] : [])].sort();
      const startDate = format(
        subDays(new Date(dts[0] + "T00:00:00"), 30),
        "yyyy-MM-dd",
      );
      const endDate = dts[dts.length - 1];
      const fetchDim = (dim: EvolutionDimension) =>
        this.api.loadEvolutionValeur("day", dim, startDate, endDate);
      forkJoin({
        total: fetchDim("total"),
        famille: fetchDim("famille"),
        sous_famille: fetchDim("sous_famille"),
        article: fetchDim("article"),
        entrepot: fetchDim("entrepot"),
        owner: fetchDim("owner"),
      }).subscribe({
        next: async (res) => {
          try {
            await exportEvolutionSnapshotExcel({ ...res, dateA: a, dateB: b });
          } finally {
            this.exporting.set(false);
          }
        },
        error: () => this.exporting.set(false),
      });
      return;
    }

    const ref = this.dialogs.open(
      ExcelExportConfirmDialogComponent,
      "fit-content",
      "fit-content",
      {
        title: "Exporter — Évolution stock",
        periodOptions: this.rangeElements,
        currentPeriod: this.range(),
        allowCustomRange: true,
      } as ExcelExportConfirmData,
    );
    const confirm = (await firstValueFrom(ref.afterClosed())) as
      | ExcelExportConfirmResult
      | null;
    if (!confirm) return;

    this.exporting.set(true);
    let startDate: string;
    let endDate: string;
    if (confirm.customStart && confirm.customEnd) {
      startDate = confirm.customStart;
      endDate = confirm.customEnd;
    } else {
      const r = (confirm.period as BackfillRange) ?? this.range();
      const range = this.resolveRange(r);
      startDate = range.startDate;
      endDate = range.endDate;
    }
    const g = this.granularity();
    const fetchDim = (dim: EvolutionDimension) =>
      this.api.loadEvolutionValeur(g, dim, startDate, endDate);

    // En plus des séries temporelles, on charge l'état courant via le
    // widget VALEUR_PAR_FAMILLE existant — sa réponse contient les
    // `details: ValeurStockDetail[]` (un par lot/stock) qui permettent
    // de construire les drill-downs hiérarchiques (famille → article →
    // lot, entrepôt → article → lot…). Sans ça, l'historique est
    // top-level uniquement (la table de snapshot ne stocke que les
    // agrégats par dimension, pas la décomposition fine).
    forkJoin({
      total: fetchDim("total"),
      famille: fetchDim("famille"),
      sous_famille: fetchDim("sous_famille"),
      article: fetchDim("article"),
      entrepot: fetchDim("entrepot"),
      owner: fetchDim("owner"),
      current: this.api.loadOne(WidgetType.VALEUR_PAR_FAMILLE),
    }).subscribe({
      next: async (res) => {
        try {
          const currentData = res.current?.data?.data as VALEUR_PAR_FAMILLE_DATA;
          await exportEvolutionValeurExcel({
            total: res.total,
            famille: res.famille,
            sous_famille: res.sous_famille,
            article: res.article,
            entrepot: res.entrepot,
            owner: res.owner,
            current: currentData,
            granularity: g,
            startDate,
            endDate,
          });
        } finally {
          this.exporting.set(false);
        }
      },
      error: () => this.exporting.set(false),
    });
  }

  refreshNow() {
    if (this.refreshing()) return;
    this.refreshing.set(true);
    this.api.runBackfill(this.range()).subscribe({
      next: (res) => {
        this.snack.success(
          TranslateUtils.translate("dashboard_ui.evolution_valeur_backfilled", {
            days: res.daysProcessed,
            count: res.rowsInserted,
            mouvements: res.mouvementsProcessed,
          }),
        );
        this.fetch();
        this.refreshing.set(false);
      },
      error: () => this.refreshing.set(false),
    });
  }

  // ─── Chart ───────────────────────────────────────────────────────────────

  private buildChart(
    series: Array<{ dimensionKey: string; dimensionLabel: string; color: string; points: { date: string; total: number }[] }>,
    labels: string[],
  ) {
    const canvas = this.canvasRef()?.nativeElement;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (this.chart) this.chart.destroy();

    // ─── Mode INSTANTANÉ (date précise) : histogramme, une barre par
    //     dimension = valeur à la date de référence. ────────────────────────
    if (this.snapshotMode()) {
      const compare = this.hasCompare();
      const datasets = compare
        ? [
            {
              label: `Au ${this.refDateDisplay()}`,
              data: series.map((s) => this.currentValue(s.points)),
              backgroundColor: "rgba(46, 161, 122, 0.85)", // A = valid
              borderColor: "rgba(46, 161, 122, 1)",
              borderWidth: 1,
              borderRadius: 4,
            },
            {
              label: `Au ${this.compareDateDisplay()}`,
              data: series.map((s) => this.compareValue(s.points)),
              backgroundColor: "rgba(154, 160, 166, 0.7)", // B = gris
              borderColor: "rgba(154, 160, 166, 1)",
              borderWidth: 1,
              borderRadius: 4,
            },
          ]
        : [
            {
              label: `Au ${this.refDateDisplay()}`,
              data: series.map((s) => this.currentValue(s.points)),
              backgroundColor: series.map((s) => s.color + "cc"),
              borderColor: series.map((s) => s.color),
              borderWidth: 1,
              borderRadius: 4,
            },
          ];
      // Étiquette de valeur au-dessus de CHAQUE barre : indispensable ici car
      // une dimension peut écraser les autres (ex. BLOC SAS 125k€ vs zones à
      // quelques €) → les petites barres sont invisibles mais restent lisibles.
      const valueLabelPlugin = {
        id: "barValueLabels",
        afterDatasetsDraw: (chart: Chart) => {
          const c = chart as unknown as {
            ctx: CanvasRenderingContext2D;
            data: { datasets: { data: (number | null)[] }[] };
            getDatasetMeta: (i: number) => { data: { x: number; y: number }[] };
          };
          const { ctx: cx } = c;
          cx.save();
          cx.font = "600 10px sans-serif";
          cx.textAlign = "center";
          c.data.datasets.forEach((ds, di) => {
            const meta = c.getDatasetMeta(di);
            meta.data.forEach((bar, i) => {
              const val = ds.data[i];
              if (val == null) return;
              const text = WidgetHelperService.formatNumeric(val) + " €";
              if (bar.y < 16) {
                // Barre trop haute → étiquette DANS la barre (texte blanc).
                cx.fillStyle = "rgba(255,255,255,0.95)";
                cx.fillText(text, bar.x, bar.y + 12);
              } else {
                cx.fillStyle = "rgba(0,0,0,0.7)";
                cx.fillText(text, bar.x, bar.y - 4);
              }
            });
          });
          cx.restore();
        },
      };
      this.chart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: series.map((s) => s.dimensionLabel),
          datasets,
        },
        plugins: [valueLabelPlugin],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: compare, position: "bottom" },
            tooltip: {
              callbacks: {
                label: (item) =>
                  `${item.dataset.label ? item.dataset.label + " : " : ""}${WidgetHelperService.formatNumeric(item.parsed.y as number)} €`,
              },
            },
          },
          scales: {
            x: { ticks: { font: { size: 10 }, autoSkip: false }, grid: { display: false } },
            y: {
              beginAtZero: true,
              ticks: {
                callback: (v) => WidgetHelperService.formatNumeric(v as number) + " €",
                font: { size: 10 },
              },
              grid: { color: "rgba(0,0,0,0.06)" },
            },
          },
        },
      });
      return;
    }

    // Map date → valeur pour chaque série, en remplissant les trous avec
    // la dernière valeur connue (forward-fill) — évite les "lignes
    // pointillées" quand une dimension n'a pas de point sur une période.
    const datasets = series.map((s) => {
      const map = new Map(s.points.map((p) => [p.date, p.total]));
      let lastVal: number | null = null;
      const data = labels.map((d) => {
        const v = map.get(d);
        if (v != null) lastVal = v;
        return lastVal ?? null;
      });
      return {
        label: s.dimensionLabel,
        data,
        borderColor: s.color,
        backgroundColor: s.color + "22",
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.2,
        spanGaps: true,
        fill: this.dimension() === "total",
      };
    });

    this.chart = new Chart(ctx, {
      type: "line",
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: this.dimension() !== "total", position: "bottom" },
          tooltip: {
            callbacks: {
              label: (item) => {
                const v = item.parsed.y as number;
                return `${item.dataset.label}: ${WidgetHelperService.formatNumeric(v)} €`;
              },
            },
          },
        },
        scales: {
          x: {
            ticks: {
              autoSkip: true,
              maxTicksLimit: 8,
              font: { size: 10 },
            },
            grid: { display: false },
          },
          y: {
            beginAtZero: false,
            ticks: {
              callback: (v) => WidgetHelperService.formatNumeric(v as number) + " €",
              font: { size: 10 },
            },
            grid: { color: "rgba(0,0,0,0.06)" },
          },
        },
      },
    });
  }

  // ─── Helpers template ────────────────────────────────────────────────────

  formatVal(v: number | null | undefined): string {
    if (v == null) return "—";
    return WidgetHelperService.formatNumeric(v) + " €";
  }

  /** Valeur d'une série à la date de référence ("valeur actuelle").
   *  Si la dimension n'a aucun point à cette date (ex : un entrepôt vidé en
   *  cours de période), la valeur est 0 — et NON le dernier point connu, qui
   *  serait une valeur figée obsolète gonflant la somme des dimensions vs le
   *  total réel. */
  currentValue(points: { date: string; total: number }[]): number {
    const ref = this.anchorDate();
    if (ref === null) return points[points.length - 1]?.total ?? 0;
    return points.find((p) => p.date === ref)?.total ?? 0;
  }

  /** Valeur d'une série à la 2ᵉ date (comparaison). 0 si aucun point à cette
   *  date (dimension vidée) — même sémantique que `currentValue`. */
  compareValue(points: { date: string; total: number }[]): number {
    const ref = this.compareAnchorDate();
    if (ref === null) return 0;
    return points.find((p) => p.date === ref)?.total ?? 0;
  }

  /** Écart entre la date A (référence) et la date B (comparaison) :
   *  état(A) − état(B), en logique DATE FIXE (pas "période"). */
  snapshotDelta(points: { date: string; total: number }[]): {
    val: number;
    pct: number | null;
    cls: "positive" | "negative" | "neutral";
  } {
    const a = this.currentValue(points);
    const b = this.compareValue(points);
    const val = a - b;
    const pct = b === 0 ? null : Math.round((val / b) * 1000) / 10;
    const cls: "positive" | "negative" | "neutral" =
      val === 0 ? "neutral" : val > 0 ? "positive" : "negative";
    return { val, pct, cls };
  }

  /** Variation (delta absolu) d'une série sur la fenêtre. La valeur de fin est
   *  prise à la date de référence (0 si la dimension a disparu) pour rester
   *  cohérente avec la "valeur actuelle" affichée. */
  seriesDelta(points: { date: string; total: number }[]): {
    val: number;
    pct: number | null;
    cls: "positive" | "negative" | "neutral";
  } {
    if (points.length === 0) return { val: 0, pct: null, cls: "neutral" };
    const start = points[0].total;
    const end = this.currentValue(points);
    const val = end - start;
    const pct = start === 0 ? null : Math.round((val / start) * 1000) / 10;
    const cls: "positive" | "negative" | "neutral" =
      val === 0 ? "neutral" : val > 0 ? "positive" : "negative";
    return { val, pct, cls };
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
